import { readCookie } from './cookies';

// GA4's default session timeout. A cookie older than this names a session that
// has already ended.
const GA_SESSION_TIMEOUT_MS = 30 * 60 * 1000;

/**
 * `_ga=GA1.1.811133795.1787690037` -> `811133795.1787690037`
 * The leading segments encode cookie version and domain depth, so the client id
 * is always the last two dot-separated parts.
 */
export function readGaClientId(): string {
  const parts = readCookie('_ga').split('.');

  return parts.length >= 4 ? parts.slice(-2).join('.') : '';
}

/**
 * `_ga_<CONTAINER>` holds the session state in one of two shapes:
 *   GS1.1.1787690037.1.1.1787690847.0.0.0
 *   GS2.1.s1787690037$o1$g1$t1787690847$j0$l0$h0
 * In both, the session id is the third dot-separated segment (GS2 prefixes it
 * with `s` and appends `$`-delimited counters) and the last hit timestamp is
 * `$t...` for GS2 or the sixth dot-separated field for GS1.
 *
 * GA4 ends a session after 30 minutes of inactivity, so a stale cookie names a
 * session that no longer exists. Unlike the client id, which is stable for
 * years, an expired session id is worse than no session id at all - it would be
 * reported against a dead session.
 */
export function readGaSessionId(measurementId: string): string {
  if (!measurementId) return '';

  const parts = readCookie(`_ga_${measurementId.replace(/^G-/, '')}`).split(
    '.',
  );
  const payload = parts[2] || '';

  const sessionId = payload.match(/^s?(\d+)/)?.[1] || '';
  const lastHit = payload.match(/\$t(\d+)/)?.[1] || parts[5] || '';

  if (!sessionId || !lastHit) return '';

  if (Date.now() - Number(lastHit) * 1000 > GA_SESSION_TIMEOUT_MS) return '';

  return sessionId;
}
